import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { QuoteService } from './quote.service';
import { HlmIconComponent } from '../../ui/icon.component';

/**
 * Daily quote card for the landing page (EB-3621 Plan 5) — de-spiked from `spike-quote`.
 * Collapsible; the quote is deterministic per day (QuoteService).
 */
@Component({
  selector: 'cmt-quote-of-the-day',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmIconComponent],
  template: `
    <article
      class="group relative overflow-hidden rounded-2xl border border-border bg-card p-7 shadow-[0_1px_2px_rgba(20,18,30,0.04),0_8px_32px_-12px_rgba(20,18,30,0.10)] transition-all duration-500 hover:shadow-[0_2px_4px_rgba(20,18,30,0.06),0_24px_48px_-16px_rgba(20,18,30,0.18)] hover:-translate-y-0.5 cmt-reveal"
      style="animation-delay:240ms"
    >
      <!-- Decorative oversized opening quote -->
      <span
        class="font-display pointer-events-none absolute -left-2 -top-6 select-none text-[10rem] font-medium leading-none text-accent/25"
        aria-hidden="true"
      >“</span>

      <header class="relative flex items-center justify-between">
        <span class="font-mono-feat text-sm uppercase tracking-[0.22em] text-muted-foreground">
          Daily Quote
        </span>
        <button
          type="button"
          (click)="toggle()"
          class="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          [attr.aria-label]="collapsed() ? 'Expand quote' : 'Collapse quote'"
        >
          <hlm-icon [name]="collapsed() ? 'lucideChevronDown' : 'lucideChevronUp'" size="1rem" />
        </button>
      </header>

      @if (!collapsed()) {
        <div class="relative mt-5">
          <blockquote class="font-display text-[1.35rem] font-normal italic leading-snug text-foreground">
            {{ quote.text }}
          </blockquote>
          <footer class="mt-5 flex items-center gap-2">
            <span class="h-px w-8 bg-accent/60"></span>
            <cite class="not-italic text-sm font-medium tracking-wide text-muted-foreground">
              {{ quote.author }}
            </cite>
          </footer>
        </div>
      }
    </article>
  `,
})
export class QuoteOfTheDayComponent {
  private readonly quoteService = inject(QuoteService);

  protected readonly quote = this.quoteService.getQuoteOfTheDay();
  protected readonly collapsed = signal(false);

  protected toggle(): void {
    this.collapsed.update((v) => !v);
  }
}
